import { roundPoint, formatNumber } from './number';
import { createPath, createVerticalPath } from './path';
import {
    generateLegendBackground, getDefaultColors, createSVGElement, setAttrs, removeAttrs,
} from './dom';
import { generateRandomIdString } from './random';
import type {
    FunnelColor,
    FunnelDirection,
    FunnelGraphData,
    FunnelGraphOptions,
    FunnelGraphUpdate,
    FunnelValues,
    SubLabelValueMode,
} from './types';

function is2dValues(values: FunnelValues): values is number[][] {
    return Array.isArray(values[0]);
}

/**
 * Typed, vendored port of funnel-graph-js@1.4.2 (MIT). See ATTRIBUTION.md for
 * what changed from upstream. Draws an SVG funnel — a trapezoid path per
 * stage, gradient-filled, with a labels layer positioned above it.
 */
export class FunnelGraph {
    containerSelector: string | HTMLElement;
    gradientDirection: FunnelDirection;
    direction: FunnelDirection;
    labels: string[];
    subLabels: string[];
    values: FunnelValues;
    percentages: number[];
    colors: FunnelColor | FunnelColor[];
    displayPercent: boolean;
    data: FunnelGraphData;
    height?: number;
    width?: number;
    subLabelValue: SubLabelValueMode;
    container!: HTMLElement;
    graphContainer!: HTMLElement;

    constructor(options: FunnelGraphOptions) {
        this.containerSelector = options.container;
        this.gradientDirection = options.gradientDirection === 'vertical' ? 'vertical' : 'horizontal';
        this.direction = options.direction === 'vertical' ? 'vertical' : 'horizontal';
        this.labels = options.data.labels ?? [];
        this.subLabels = options.data.subLabels ?? [];
        this.values = options.data.values ?? [];
        this.percentages = this.createPercentages();
        this.colors = options.data.colors ?? getDefaultColors(this.is2d() ? this.getSubDataSize() : 2);
        this.displayPercent = options.displayPercent ?? false;
        this.data = options.data;
        this.height = options.height;
        this.width = options.width;
        this.subLabelValue = options.subLabelValue ?? 'percent';
    }

    getMainAxisPoints(): number[] {
        const size = this.getDataSize();
        const points: number[] = [];
        const fullDimension = this.isVertical() ? this.getHeight() : this.getWidth();

        for (let i = 0; i <= size; i++) {
            points.push(roundPoint((fullDimension * i) / size));
        }

        return points;
    }

    getCrossAxisPoints(): number[][] {
        const points: number[][] = [];
        const fullDimension = this.getFullDimension();
        // Half the graph's cross dimension — the funnel shape is symmetric around its centerline.
        const dimension = fullDimension / 2;

        if (this.is2d()) {
            const totalValues = this.getValues2d();
            const max = Math.max(...totalValues);

            const withDuplicate = [...totalValues, totalValues[totalValues.length - 1]];
            points.push(withDuplicate.map((value) => roundPoint(((max - value) / max) * dimension)));

            const percentagesFull = this.getPercentages2d();
            const pointsOfFirstPath = points[0];

            for (let i = 1; i < this.getSubDataSize(); i++) {
                const previous = points[i - 1];
                const newPoints: number[] = [];

                for (let j = 0; j < this.getDataSize(); j++) {
                    newPoints.push(roundPoint(
                        previous[j] + (fullDimension - pointsOfFirstPath[j] * 2) * (percentagesFull[j][i - 1] / 100),
                    ));
                }

                newPoints.push(newPoints[newPoints.length - 1]);
                points.push(newPoints);
            }

            points.push(pointsOfFirstPath.map((point) => fullDimension - point));
        } else {
            const values = this.values as number[];
            const max = Math.max(...values);
            const withDuplicate = [...values, values[values.length - 1]];

            points.push(withDuplicate.map((value) => roundPoint(((max - value) / max) * dimension)));
            points.push(points[0].map((point) => fullDimension - point));
        }

        return points;
    }

    getGraphType(): 'normal' | '2d' {
        return is2dValues(this.values) ? '2d' : 'normal';
    }

    is2d(): boolean {
        return this.getGraphType() === '2d';
    }

    isVertical(): boolean {
        return this.direction === 'vertical';
    }

    getDataSize(): number {
        return this.values.length;
    }

    getSubDataSize(): number {
        return (this.values[0] as number[]).length;
    }

    getFullDimension(): number {
        return this.isVertical() ? this.getWidth() : this.getHeight();
    }

    addLabels(): void {
        const holder = document.createElement('div');
        holder.setAttribute('class', 'svg-funnel-js__labels');

        this.percentages.forEach((percentage, index) => {
            const labelElement = document.createElement('div');
            labelElement.setAttribute('class', `svg-funnel-js__label label-${index + 1}`);

            const title = document.createElement('div');
            title.setAttribute('class', 'label__title');
            title.textContent = this.labels[index] || '';

            const value = document.createElement('div');
            value.setAttribute('class', 'label__value');
            const valueNumber = this.is2d() ? this.getValues2d()[index] : (this.values as number[])[index];
            value.textContent = formatNumber(valueNumber);

            const percentageValue = document.createElement('div');
            percentageValue.setAttribute('class', 'label__percentage');
            percentageValue.textContent = `${percentage}%`;

            labelElement.appendChild(value);
            labelElement.appendChild(title);
            if (this.displayPercent) {
                labelElement.appendChild(percentageValue);
            }

            if (this.is2d()) {
                const segmentPercentages = document.createElement('div');
                segmentPercentages.setAttribute('class', 'label__segment-percentages');
                const twoDimPercentages = this.getPercentages2d();

                let percentageList = '<ul class="segment-percentage__list">';
                this.subLabels.forEach((subLabel, j) => {
                    const subLabelDisplayValue = this.subLabelValue === 'percent'
                        ? `${twoDimPercentages[index][j]}%`
                        : formatNumber((this.values as number[][])[index][j]);
                    percentageList += `<li>${subLabel}: `
                        + `<span class="percentage__list-label">${subLabelDisplayValue}</span></li>`;
                });
                percentageList += '</ul>';

                segmentPercentages.innerHTML = percentageList;
                labelElement.appendChild(segmentPercentages);
            }

            holder.appendChild(labelElement);
        });

        this.container.appendChild(holder);
    }

    addSubLabels(): void {
        if (!this.subLabels.length) return;

        const subLabelsHolder = document.createElement('div');
        subLabelsHolder.setAttribute('class', 'svg-funnel-js__subLabels');

        let html = '';
        this.subLabels.forEach((subLabel, index) => {
            const color = (this.colors as FunnelColor[])[index];
            html += `<div class="svg-funnel-js__subLabel svg-funnel-js__subLabel-${index + 1}">`
                + `<div class="svg-funnel-js__subLabel--color" `
                + `style="${generateLegendBackground(color, this.gradientDirection)}"></div>`
                + `<div class="svg-funnel-js__subLabel--title">${subLabel}</div></div>`;
        });

        subLabelsHolder.innerHTML = html;
        this.container.appendChild(subLabelsHolder);
    }

    createContainer(): void {
        const el = typeof this.containerSelector === 'string'
            ? document.querySelector<HTMLElement>(this.containerSelector)
            : this.containerSelector;

        if (!el) {
            throw new Error(`Container cannot be found (selector: ${String(this.containerSelector)}).`);
        }

        this.container = el;
        this.container.classList.add('svg-funnel-js');

        this.graphContainer = document.createElement('div');
        this.graphContainer.classList.add('svg-funnel-js__container');
        this.container.appendChild(this.graphContainer);

        if (this.direction === 'vertical') {
            this.container.classList.add('svg-funnel-js--vertical');
        }
    }

    setValues(values: FunnelValues): this {
        this.values = values;
        return this;
    }

    setDirection(direction: FunnelDirection): this {
        this.direction = direction;
        return this;
    }

    setHeight(height: number): this {
        this.height = height;
        return this;
    }

    setWidth(width: number): this {
        this.width = width;
        return this;
    }

    getValues2d(): number[] {
        return (this.values as number[][]).map((valueSet) => valueSet.reduce((sum, value) => sum + value, 0));
    }

    getPercentages2d(): number[][] {
        return (this.values as number[][]).map((valueSet) => {
            const total = valueSet.reduce((sum, value) => sum + value, 0);
            return valueSet.map((value) => (total === 0 ? 0 : roundPoint((value * 100) / total)));
        });
    }

    createPercentages(): number[] {
        const values = this.is2d() ? this.getValues2d() : [...(this.values as number[])];
        const max = Math.max(...values);
        return values.map((value) => (value === 0 ? 0 : roundPoint((value * 100) / max)));
    }

    applyGradient(svg: SVGSVGElement, path: SVGPathElement, colors: string[], index: number): void {
        const defs = svg.querySelector('defs') ?? createSVGElement('defs', svg);
        const gradientName = generateRandomIdString(`funnelGradient-${index}-`);
        const gradient = createSVGElement('linearGradient', defs, { id: gradientName });

        if (this.gradientDirection === 'vertical') {
            setAttrs(gradient, { x1: '0', x2: '0', y1: '0', y2: '1' });
        }

        colors.forEach((color, i) => {
            createSVGElement('stop', gradient, {
                'stop-color': color,
                offset: `${Math.round((100 * i) / (colors.length - 1))}%`,
            });
        });

        setAttrs(path, { fill: `url("#${gradientName}")`, stroke: `url("#${gradientName}")` });
    }

    makeSVG(): void {
        const svg = createSVGElement('svg', this.graphContainer, {
            width: this.getWidth(),
            height: this.getHeight(),
        });

        const valuesNum = this.getCrossAxisPoints().length - 1;
        for (let i = 0; i < valuesNum; i++) {
            const path = createSVGElement('path', svg);
            const color = this.is2d() ? (this.colors as FunnelColor[])[i] : (this.colors as FunnelColor);
            const isSolid = typeof color === 'string' || color.length === 1;

            if (isSolid) {
                const solid = typeof color === 'string' ? color : color[0];
                setAttrs(path, { fill: solid, stroke: solid });
            } else {
                this.applyGradient(svg, path, color, i + 1);
            }
        }

        this.graphContainer.appendChild(svg);
    }

    getSVG(): SVGSVGElement {
        const svg = this.container.querySelector('svg');
        if (!svg) throw new Error('No SVG found inside of the container');
        return svg;
    }

    getWidth(): number {
        return this.width || this.graphContainer.clientWidth;
    }

    getHeight(): number {
        return this.height || this.graphContainer.clientHeight;
    }

    getPathDefinitions(): string[] {
        const valuesNum = this.getCrossAxisPoints().length - 1;
        const paths: string[] = [];

        for (let i = 0; i < valuesNum; i++) {
            if (this.isVertical()) {
                const X = this.getCrossAxisPoints()[i];
                const XNext = this.getCrossAxisPoints()[i + 1];
                const Y = this.getMainAxisPoints();
                paths.push(createVerticalPath(X, XNext, Y));
            } else {
                const X = this.getMainAxisPoints();
                const Y = this.getCrossAxisPoints()[i];
                const YNext = this.getCrossAxisPoints()[i + 1];
                paths.push(createPath(X, Y, YNext));
            }
        }

        return paths;
    }

    drawPaths(): void {
        const svg = this.getSVG();
        const paths = svg.querySelectorAll('path');
        this.getPathDefinitions().forEach((definition, index) => {
            paths[index]?.setAttribute('d', definition);
        });
    }

    draw(): void {
        this.createContainer();
        this.makeSVG();
        this.addLabels();
        if (this.is2d()) this.addSubLabels();
        this.drawPaths();
    }

    makeVertical(): void {
        if (this.direction === 'vertical') return;
        this.direction = 'vertical';
        this.container.classList.add('svg-funnel-js--vertical');
        setAttrs(this.getSVG(), { height: this.getHeight(), width: this.getWidth() });
        this.drawPaths();
    }

    makeHorizontal(): void {
        if (this.direction === 'horizontal') return;
        this.direction = 'horizontal';
        this.container.classList.remove('svg-funnel-js--vertical');
        setAttrs(this.getSVG(), { height: this.getHeight(), width: this.getWidth() });
        this.drawPaths();
    }

    toggleDirection(): void {
        if (this.direction === 'horizontal') this.makeVertical();
        else this.makeHorizontal();
    }

    gradientMakeVertical(): void {
        if (this.gradientDirection === 'vertical') return;
        this.gradientDirection = 'vertical';
        this.graphContainer.querySelectorAll('linearGradient').forEach((gradient) => {
            setAttrs(gradient, { x1: '0', x2: '0', y1: '0', y2: '1' });
        });
    }

    gradientMakeHorizontal(): void {
        if (this.gradientDirection === 'horizontal') return;
        this.gradientDirection = 'horizontal';
        this.graphContainer.querySelectorAll('linearGradient').forEach((gradient) => {
            removeAttrs(gradient, 'x1', 'x2', 'y1', 'y2');
        });
    }

    gradientToggleDirection(): void {
        if (this.gradientDirection === 'horizontal') this.gradientMakeVertical();
        else this.gradientMakeHorizontal();
    }

    updateWidth(width: number): void {
        this.width = width;
        setAttrs(this.getSVG(), { width: this.getWidth() });
        this.drawPaths();
    }

    updateHeight(height: number): void {
        this.height = height;
        setAttrs(this.getSVG(), { height: this.getHeight() });
        this.drawPaths();
    }

    updateData(next: FunnelGraphData): void {
        this.container.querySelector('.svg-funnel-js__labels')?.remove();
        this.container.querySelector('.svg-funnel-js__subLabels')?.remove();

        const wasSameShape = is2dValues(this.values) === is2dValues(next.values);

        this.labels = next.labels ?? [];
        this.subLabels = next.subLabels ?? this.subLabels;
        this.values = next.values;
        this.colors = next.colors ?? getDefaultColors(this.is2d() ? this.getSubDataSize() : 2);

        if (!wasSameShape) {
            this.container.querySelector('svg')?.remove();
            this.makeSVG();
        }

        this.percentages = this.createPercentages();
        this.drawPaths();
        this.addLabels();

        if (next.subLabels) this.addSubLabels();
    }

    update(next: FunnelGraphUpdate): void {
        if (next.displayPercent !== undefined && next.displayPercent !== this.displayPercent) {
            this.displayPercent = next.displayPercent;

            if (!this.displayPercent) {
                this.container.querySelectorAll('.label__percentage').forEach((label) => label.remove());
            } else {
                this.container.querySelectorAll('.svg-funnel-js__label').forEach((label, index) => {
                    const percentage = this.percentages[index];
                    if (percentage === 100) return;
                    const percentageValue = document.createElement('div');
                    percentageValue.setAttribute('class', 'label__percentage');
                    percentageValue.textContent = `${percentage}%`;
                    label.appendChild(percentageValue);
                });
            }
        }

        if (next.height !== undefined) this.updateHeight(next.height);
        if (next.width !== undefined) this.updateWidth(next.width);

        if (next.gradientDirection === 'vertical') this.gradientMakeVertical();
        else if (next.gradientDirection === 'horizontal') this.gradientMakeHorizontal();

        if (next.direction === 'vertical') this.makeVertical();
        else if (next.direction === 'horizontal') this.makeHorizontal();

        if (next.data) this.updateData(next.data);
    }
}

export default FunnelGraph;
